import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Target,
  ListTodo,
  CheckSquare,
  ArrowUpRight,
  Globe,
  AlertCircle,
  Clock,
  CheckCircle2,
  MousePointerClick,
  Eye,
  TrendingUp,
  Hash,
  Loader2,
  FileSearch,
  XCircle,
  Play,
  CheckCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function SEODashboard() {
  const { user } = useAuth();
  const { selectedProjectId } = useProject();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [onpageRecommendations, setOnpageRecommendations] = useState([]);
  const [gscData, setGscData] = useState(null);
  const [stats, setStats] = useState({
    assignedTasks: 0,
    completedTasks: 0,
    pendingImplementations: 0,
    inProgress: 0
  });
  const [indexation, setIndexation] = useState(null);
  const [indexationLoading, setIndexationLoading] = useState(false);
  const [indexChecking, setIndexChecking] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const projectParams = selectedProjectId ? { project_id: selectedProjectId } : {};
      let tasksList = [];
      try {
        const tasksRes = await api.get('/tasks', {
          params: { page_size: 200, ...projectParams },
        });
        tasksList = tasksRes.data?.items || [];
      } catch (e) {
        // Tasks might be empty
      }
      setTasks(tasksList);

      // Fetch on-page recommendations that are approved but not implemented
      let recommendations = [];
      try {
        const recsRes = await api.get('/onpage/recommendations/approved', {
          params: { limit: 100, ...projectParams },
        });
        recommendations = recsRes.data || [];
      } catch (e) {
        // On-page might be empty
      }
      setOnpageRecommendations(recommendations);

      try {
        const intRes = await api.get('/integrations/status');
        if (intRes.data?.gsc?.status === 'connected') {
          const gscRes = await api.get('/analytics/gsc/data', { params: { days: 28 } });
          setGscData(gscRes.data);
        }
      } catch (e) {
        // GSC data not critical
      }

      setStats({
        assignedTasks: tasksList.length,
        completedTasks: tasksList.filter(t => t.status === 'done').length,
        pendingImplementations: recommendations.length,
        inProgress: tasksList.filter(t => t.status === 'in_progress').length
      });

      try {
        const idxRes = await api.get('/seo/indexation/status');
        setIndexation(idxRes.data);
      } catch (e) {
        // Indexation data not critical
      }

    } catch (error) {
      console.error('Error fetching SEO dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (user?.user_id) fetchDashboardData();
  }, [user?.user_id, fetchDashboardData]);

  const handleStartTask = async (taskId) => {
    try {
      await api.post(`/execution/task/${taskId}/start`);
      toast.success('Task started');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to start task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/execution/task/${taskId}/complete`, { notes: '' });
      toast.success('Task completed');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="seo-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">SEO Execution Dashboard</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Your assigned SEO tasks and implementations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard/tasks')}>
            <ListTodo className="w-4 h-4 mr-2" />
            All Tasks
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard/onpage')}>
            <Search className="w-4 h-4 mr-2" />
            On-Page SEO
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Open Tasks</p>
                <p className="text-2xl font-bold font-heading mt-1">{stats.assignedTasks}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">In Progress</p>
                <p className="text-2xl font-bold font-heading mt-1">{stats.inProgress}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Completed</p>
                <p className="text-2xl font-bold font-heading mt-1">{stats.completedTasks}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending Implementations</p>
                <p className="text-2xl font-bold font-heading mt-1">{stats.pendingImplementations}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Target className="w-5 h-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GSC Performance Overview */}
      {gscData && (
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-500" />
              Search Console Performance
            </CardTitle>
            <CardDescription>Last 28 days from Google Search Console</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs text-slate-500">Keywords</span>
                </div>
                <p className="text-xl font-bold">{gscData.overview?.totalKeywords || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-slate-500">Top 10</span>
                </div>
                <p className="text-xl font-bold">{gscData.overview?.top10 || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20">
                <div className="flex items-center gap-2 mb-1">
                  <MousePointerClick className="w-4 h-4 text-cyan-500" />
                  <span className="text-xs text-slate-500">Clicks</span>
                </div>
                <p className="text-xl font-bold">{gscData.overview?.totalClicks?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-violet-500" />
                  <span className="text-xs text-slate-500">Impressions</span>
                </div>
                <p className="text-xl font-bold">{gscData.overview?.totalImpressions?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-slate-500">Avg Position</span>
                </div>
                <p className="text-xl font-bold">{gscData.overview?.avgPosition ? Math.round(gscData.overview.avgPosition) : '--'}</p>
                <p className="text-xs text-slate-500">CTR: {gscData.overview?.avgCTR || 0}%</p>
              </div>
            </div>

            {gscData.topQueries && gscData.topQueries.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Top Queries</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500">Query</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Position</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Clicks</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Impressions</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">CTR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {gscData.topQueries.slice(0, 10).map((q, i) => (
                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="py-2 px-2 font-medium">{q.query}</td>
                          <td className="py-2 px-2 text-right">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${
                              q.position <= 3 ? 'text-emerald-600 bg-emerald-50' :
                              q.position <= 10 ? 'text-blue-600 bg-blue-50' :
                              'text-slate-600 bg-slate-50'
                            }`}>
                              #{Math.round(q.position)}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right">{q.clicks.toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-slate-500">{q.impressions.toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-slate-500">{q.ctr}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* My Tasks */}
        <Card className="gds-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-indigo-500" />
                SEO Tasks
              </CardTitle>
              <CardDescription>Tasks ready for SEO oversight</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/tasks')}>
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">No tasks available</p>
                <p className="text-sm mt-1">Tasks will appear here when they are created</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div
                    key={task.task_id}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{task.title}</p>
                        <p className="text-sm text-slate-500 mt-1">{task.description?.slice(0, 60)}...</p>
                      </div>
                      <Badge className={
                        task.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {task.status?.replace('_', ' ') || 'Todo'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      {task.due_date && (
                        <p className="text-xs text-slate-400">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                      <div className="flex gap-2 ml-auto">
                        {task.status === 'todo' && (
                          <Button size="sm" variant="outline" onClick={() => handleStartTask(task.task_id)}>
                            <Play className="w-3 h-3 mr-1" />
                            Start
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Button size="sm" className="gds-btn-primary" onClick={() => handleCompleteTask(task.task_id)}>
                            <CheckCheck className="w-3 h-3 mr-1" />
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Approved On-Page Implementations */}
        <Card className="gds-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-500" />
                Approved Implementations
              </CardTitle>
              <CardDescription>On-page changes ready to implement</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/onpage')}>
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {onpageRecommendations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">No pending implementations</p>
                <p className="text-sm mt-1">Approved on-page changes will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {onpageRecommendations.slice(0, 5).map((rec) => (
                  <div 
                    key={rec.recommendation_id}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Badge variant="outline" className="mb-2">{rec.category}</Badge>
                        <p className="font-medium">{rec.title}</p>
                      </div>
                      <Badge className={
                        rec.severity === 'high' ? 'bg-red-100 text-red-700' :
                        rec.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {rec.severity}
                      </Badge>
                    </div>
                    {rec.code_snippet && (
                      <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs overflow-x-auto">
                        {rec.code_snippet.slice(0, 100)}...
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Indexation Tracking Widget */}
      {indexation && indexation.total_checked > 0 && (
        <Card className="gds-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileSearch className="w-5 h-5 text-indigo-500" />
                  Indexation Status
                </CardTitle>
                <CardDescription>
                  {indexation.total_checked} pages checked
                  {indexation.last_checked && ` · Last checked ${indexation.last_checked.split('T')[0]}`}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={indexChecking}
                onClick={async () => {
                  setIndexChecking(true);
                  try {
                    await api.post('/seo/indexation/check', { urls: [] });
                    const idxRes = await api.get('/seo/indexation/status');
                    setIndexation(idxRes.data);
                    toast.success('Indexation check complete');
                  } catch (e) {
                    toast.error('Indexation check failed');
                  } finally {
                    setIndexChecking(false);
                  }
                }}
              >
                {indexChecking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
                Check Now
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-xl font-bold text-emerald-600">{indexation.indexed}</div>
                <div className="text-xs text-slate-500">Indexed</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-2">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="text-xl font-bold text-amber-600">{indexation.not_indexed}</div>
                <div className="text-xs text-slate-500">Not Indexed</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-2">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="text-xl font-bold text-red-600">{indexation.errors}</div>
                <div className="text-xs text-slate-500">Errors</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-2">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="text-xl font-bold text-indigo-600">{indexation.index_rate}%</div>
                <div className="text-xs text-slate-500">Index Rate</div>
              </div>
            </div>
            {/* Index Rate Bar */}
            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3 mb-4">
              <div
                className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-3 rounded-full transition-all"
                style={{ width: `${Math.min(100, indexation.index_rate || 0)}%` }}
              />
            </div>
            {/* Page List */}
            {indexation.pages?.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {indexation.pages.slice(0, 15).map((page, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800/50 text-sm">
                    <span className="truncate flex-1 mr-2">{page.url?.replace(/^https?:\/\//, '')}</span>
                    <Badge className={page.is_indexed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {page.is_indexed ? 'Indexed' : page.verdict === 'ERROR' ? 'Error' : 'Not Indexed'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="gds-card">
        <CardHeader>
          <CardTitle className="font-heading">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/dashboard/onpage')}
            >
              <Search className="w-6 h-6 text-indigo-500" />
              <span>On-Page SEO</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/dashboard/tasks')}
            >
              <ListTodo className="w-6 h-6 text-violet-500" />
              <span>My Tasks</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/dashboard/calendar')}
            >
              <Clock className="w-6 h-6 text-amber-500" />
              <span>Calendar</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/dashboard/notifications')}
            >
              <AlertCircle className="w-6 h-6 text-emerald-500" />
              <span>Notifications</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
